import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import ExportButton from "@/components/ExportButton";
import { ChevronDown, ChevronRight, Search } from "lucide-react";

export const money = (v) => `$${(Number(v) || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const num = (v) => (Number(v) || 0).toLocaleString("es-EC", { maximumFractionDigits: 2 });

const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function useFiltro(rows, campos) {
  const [q, setQ] = useState("");
  const filtrados = q ? rows.filter(r => norm(campos.map(c => r[c]).join(" ")).includes(norm(q))) : rows;
  return [q, setQ, filtrados];
}

function Barra({ q, setQ, testid, title, columns, rows, filename }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="relative flex-1 max-w-sm">
        <Search size={14} className="absolute left-2 top-2.5 text-[#8ba397]" />
        <Input className="pl-8 h-9" placeholder="Filtrar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={testid} />
      </div>
      <div className="flex-1" />
      <ExportButton title={title} columns={columns} rows={rows} filename={filename} />
    </div>
  );
}

export function TablaProductos({ rows }) {
  const [q, setQ, f] = useFiltro(rows, ["producto", "lotes"]);
  const cols = [
    { label: "Producto", accessor: "producto" }, { label: "Cantidad", accessor: "cantidad" }, { label: "Monto", accessor: "monto" },
    { label: "Veces", accessor: "veces" }, { label: "Precio prom.", accessor: "precio_promedio" },
    { label: "Primera", accessor: "primera_compra" }, { label: "Última", accessor: "ultima_compra" },
    { label: "Cada (días)", accessor: "frecuencia_dias" }, { label: "Devuelto", accessor: "devuelto_cant" },
  ];
  return (
    <div data-testid="perfil-tabla-productos">
      <Barra q={q} setQ={setQ} testid="perfil-filtro-productos" title="Productos del cliente" columns={cols} rows={f} filename="cliente-productos" />
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-auto">
        <table className="erp-table">
          <thead><tr><th>Producto</th><th className="text-right">Cantidad</th><th className="text-right">Monto</th><th className="text-right">Veces</th><th className="text-right">Precio prom.</th><th>Primera</th><th>Última</th><th className="text-right">Cada</th><th className="text-right">Devuelto</th><th></th></tr></thead>
          <tbody>
            {f.map(p => (
              <tr key={p.clave} data-testid={`perfil-prod-${p.clave}`}>
                <td className="font-medium">{p.producto || "—"}{p.lotes?.length ? <div className="text-[10px] text-[#8ba397] font-mono">Lotes: {p.lotes.slice(0, 4).join(", ")}{p.lotes.length > 4 ? "…" : ""}</div> : null}</td>
                <td className="text-right tabular-nums">{num(p.cantidad)}</td>
                <td className="text-right tabular-nums font-semibold">{money(p.monto)}</td>
                <td className="text-right tabular-nums">{p.veces}</td>
                <td className="text-right tabular-nums">{money(p.precio_promedio)}</td>
                <td className="text-xs">{p.primera_compra || "—"}</td>
                <td className="text-xs">{p.ultima_compra || "—"}</td>
                <td className="text-right tabular-nums text-xs">{p.frecuencia_dias ? `${p.frecuencia_dias} d` : "—"}</td>
                <td className="text-right tabular-nums">{p.devuelto_cant ? <span className="text-[#b53a25]">{num(p.devuelto_cant)}</span> : "—"}</td>
                <td>{p.en_riesgo ? <Badge className="bg-[#fdecea] text-[#7a2514] border border-[#f2c4bb] text-[10px]">Dejó de comprar</Badge> : null}</td>
              </tr>
            ))}
            {f.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-[#8ba397]">Sin productos en el periodo</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilaFactura({ f }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className={f.valida ? "" : "opacity-60"} data-testid={`perfil-fact-${f.id}`}>
        <td><button type="button" onClick={() => setOpen(o => !o)} className="text-[#205c45]" data-testid={`perfil-fact-toggle-${f.id}`}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button></td>
        <td className="font-mono text-xs">{f.numero}</td>
        <td className="text-xs">{f.fecha}</td>
        <td className="text-xs">{f.vendedor || "—"}</td>
        <td className="text-right tabular-nums">{f.n_lineas}</td>
        <td className="text-right tabular-nums">{money(f.subtotal)}</td>
        <td className="text-right tabular-nums">{money(f.iva)}</td>
        <td className="text-right tabular-nums font-semibold">{money(f.total)}</td>
        <td className="text-right tabular-nums">{f.saldo > 0 ? <span className="text-[#8a5a00]">{money(f.saldo)}</span> : "—"}</td>
        <td className="text-xs">{f.estado === "anulada" ? <Badge variant="outline">Anulada</Badge> : (f.estado_sri || f.estado || "—")}</td>
      </tr>
      {open && (
        <tr><td colSpan={10} className="bg-[#f7f9f8]">
          <table className="w-full text-xs">
            <thead><tr className="text-[#5c7368]"><th className="text-left py-1">Producto</th><th className="text-left">Lote</th><th className="text-left">Vence</th><th className="text-right">Cant.</th><th className="text-right">P. unit.</th><th className="text-right">Subtotal</th></tr></thead>
            <tbody>{f.lineas.map((li, i) => (
              <tr key={i}><td className="py-1">{li.producto}</td><td className="font-mono">{li.numero_lote || "—"}</td><td>{li.fecha_vencimiento || "—"}</td><td className="text-right">{num(li.cantidad)}</td><td className="text-right">{money(li.precio_unitario)}</td><td className="text-right">{money(li.subtotal)}</td></tr>
            ))}</tbody>
          </table>
        </td></tr>
      )}
    </>
  );
}

export function TablaFacturas({ rows }) {
  const [q, setQ, f] = useFiltro(rows, ["numero", "vendedor", "fecha"]);
  const cols = [
    { label: "Número", accessor: "numero" }, { label: "Fecha", accessor: "fecha" }, { label: "Vendedor", accessor: "vendedor" },
    { label: "Subtotal", accessor: "subtotal" }, { label: "IVA", accessor: "iva" }, { label: "Total", accessor: "total" },
    { label: "Saldo", accessor: "saldo" }, { label: "Estado", accessor: "estado" },
  ];
  return (
    <div data-testid="perfil-tabla-facturas">
      <Barra q={q} setQ={setQ} testid="perfil-filtro-facturas" title="Facturas del cliente" columns={cols} rows={f} filename="cliente-facturas" />
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-auto">
        <table className="erp-table">
          <thead><tr><th className="w-6"></th><th>Número</th><th>Fecha</th><th>Vendedor</th><th className="text-right">Ítems</th><th className="text-right">Subtotal</th><th className="text-right">IVA</th><th className="text-right">Total</th><th className="text-right">Saldo</th><th>Estado</th></tr></thead>
          <tbody>
            {f.map(x => <FilaFactura key={x.id} f={x} />)}
            {f.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-[#8ba397]">Sin facturas en el periodo</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TablaDevoluciones({ rows }) {
  const [q, setQ, f] = useFiltro(rows, ["producto", "numero_lote", "documento", "motivo"]);
  const cols = [
    { label: "Fecha", accessor: "fecha" }, { label: "Documento", accessor: "documento" }, { label: "Factura origen", accessor: "factura_origen" },
    { label: "Producto", accessor: "producto" }, { label: "Lote", accessor: "numero_lote" }, { label: "Vence", accessor: "fecha_vencimiento" },
    { label: "Cantidad", accessor: "cantidad" }, { label: "Valor", accessor: "valor" }, { label: "Motivo", accessor: "motivo" },
  ];
  return (
    <div data-testid="perfil-tabla-devoluciones">
      <Barra q={q} setQ={setQ} testid="perfil-filtro-devoluciones" title="Devoluciones del cliente" columns={cols} rows={f} filename="cliente-devoluciones" />
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-auto">
        <table className="erp-table">
          <thead><tr><th>Fecha</th><th>Documento</th><th>Fact. origen</th><th>Producto</th><th>Lote</th><th>Vence</th><th className="text-right">Cant.</th><th className="text-right">Valor</th><th>Motivo</th></tr></thead>
          <tbody>
            {f.map((d, i) => (
              <tr key={i} data-testid={`perfil-dev-${i}`}>
                <td className="text-xs">{d.fecha}</td>
                <td className="font-mono text-xs">{d.documento}<div className="text-[10px] text-[#8ba397] font-sans">{d.origen}</div></td>
                <td className="font-mono text-xs">{d.factura_origen || "—"}</td>
                <td className="font-medium">{d.producto || "—"}</td>
                <td className="font-mono text-xs">{d.numero_lote || "—"}</td>
                <td className="text-xs">{d.fecha_vencimiento || "—"}</td>
                <td className="text-right tabular-nums">{num(d.cantidad)}</td>
                <td className="text-right tabular-nums">{money(d.valor)}</td>
                <td className="text-xs text-[#4a5c53] max-w-[220px] truncate" title={d.motivo}>{d.motivo || "—"}</td>
              </tr>
            ))}
            {f.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-[#8ba397]">Sin devoluciones en el periodo</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
